import { useEffect, useState } from "react"
import { useTenant } from "../../context/tenantContext"
import { Navigate } from "react-router-dom"
import { getAllTenantDetails } from "../../api/tenant"

import { Box, Typography } from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"

const Retries = process.env.TENANT_LIST_RETRY_DELAY_MS
const MaxEntries = process.env.TENANT_LIST_MAX_RETRIES

export default function TenantGuard({ children }) {
  const { tenant } = useTenant()
  const [allowedTenants, setAllowedTenants] = useState(() => {
    try {
      const cached = sessionStorage.getItem("allowedTenantSlugs")
      const parsed = cached ? JSON.parse(cached) : []
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  })
  const [loadingAllowedTenants, setLoadingAllowedTenants] = useState(
    () => allowedTenants.length === 0,
  )

  useEffect(() => {
    let cancelled = false

    const getCachedSlugs = () => {
      try {
        const cached = sessionStorage.getItem("allowedTenantSlugs")
        const parsed = cached ? JSON.parse(cached) : []
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return []
      }
    }

    const loadAllowedTenants = async (attempt = 0) => {
      try {
        const response = await getAllTenantDetails()
        const allTenants = response?.data || []
        const slugs = allTenants
          .map((item) =>
            String(item?.slug || "")
              .trim()
              .toLowerCase(),
          )
          .filter(Boolean)

        if (!cancelled) {
          setAllowedTenants(slugs)
          setLoadingAllowedTenants(false)
        }

        sessionStorage.setItem("allowedTenantSlugs", JSON.stringify(slugs))
      } catch (error) {
        const fallbackSlugs = getCachedSlugs()

        if (!cancelled && fallbackSlugs.length > 0) {
          setAllowedTenants(fallbackSlugs)
          setLoadingAllowedTenants(false)
          return
        }

        if (!cancelled && attempt < MaxEntries) {
          window.setTimeout(() => {
            loadAllowedTenants(attempt + 1)
          }, Retries)
          return
        }

        if (!cancelled) {
          setLoadingAllowedTenants(false)
        }
      }
    }

    loadAllowedTenants()

    return () => {
      cancelled = true
    }
  }, [])

  const resolvedTenant = String(
    sessionStorage.getItem("tenant") || tenant || "",
  )
    .trim()
    .toLowerCase()

  if (loadingAllowedTenants) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body1" color="text.subcaption">
          Loading tenant...
        </Typography>
      </Box>
    )
  }

  if (!resolvedTenant) {
    return <Navigate to="/" replace />
  }

  if (allowedTenants.length > 0 && !allowedTenants.includes(resolvedTenant)) {
    return <Navigate to="/unauthorized" replace />
  }

  return children
}
